import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Heart, Search, ShoppingBag, User, UserCog, LogOut, Shield, Package, Menu } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useShop } from "@/context/ShopContext";
import { ACCESSORY_TYPES, GAME_PAGES } from "@/lib/constants";
import { LanguageSwitcher } from "@/components/shop/LanguageSwitcher";
import { SearchSuggest } from "@/components/shop/SearchSuggest";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount, setCartOpen, wishlist } = useShop();
  const [showSearch, setShowSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-50 border-b border-black/10 bg-[#FFFBF3]/80 backdrop-blur-xl">
      <div className="relative flex min-h-[3.5rem] w-full flex-wrap items-center gap-x-4 gap-y-2 py-2 sm:flex-nowrap sm:py-1 px-4 sm:px-6">
        <Link to="/" className="relative flex h-12 shrink-0 items-center gap-2 md:w-28" data-testid="nav-logo">
          <img src="/brand/logo.png" alt="Card Source - La tua fonte di sigillati TCG" className="h-12 w-auto object-contain drop-shadow-[0_4px_6px_rgba(0,0,0,0.35)] transition-transform md:absolute md:left-0 md:top-0 md:z-10 md:h-36 hover:-rotate-3 hover:scale-105" />
        </Link>
        <SearchSuggest visible={showSearch} />
        <button type="button" onClick={() => setShowSearch((v) => !v)} className="ml-auto rounded-full p-2 hover:bg-black/5 sm:hidden" aria-label="Cerca" data-testid="nav-search-toggle"><Search className="h-5 w-5" /></button>
        <span className="hidden sm:ml-auto sm:block" />
        <LanguageSwitcher />
        <Link to={user ? "/account?tab=wishlist" : "/accedi"} className="relative rounded-full p-2 hover:bg-black/5" data-testid="nav-wishlist-button">
          <Heart className="h-5 w-5" />
          {wishlist.length > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold">{wishlist.length}</span>}
        </Link>
        <button onClick={() => setCartOpen(true)} className="relative rounded-full p-2 hover:bg-black/5" data-testid="nav-cart-button">
          <ShoppingBag className="h-5 w-5" />
          {cartCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black" data-testid="nav-cart-count">{cartCount}</span>}
        </button>
        {user ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger data-no-translate className="flex items-center gap-1.5 rounded-full border border-black/10 p-0.5 pr-2.5 hover:border-amber-500/40" data-testid="nav-user-menu">
              {user.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet-500 text-xs font-bold text-white">{user.name?.[0]?.toUpperCase()}</span>}
              <span className="hidden max-w-[100px] truncate text-xs md:block">{user.name}</span>
              {user.account_type === "b2b" && <span className="rounded-full bg-cyan-700 px-1.5 text-[10px] font-bold text-white" data-testid="nav-b2b-badge">B2B</span>}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 border-black/10 bg-[#FFFFFF]">
              <DropdownMenuItem onClick={() => navigate("/account?tab=profilo")} data-testid="nav-profile-link"><UserCog className="mr-2 h-4 w-4" />Profilo</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account")} data-testid="nav-account-link"><Package className="mr-2 h-4 w-4" />I miei ordini</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account?tab=wishlist")}><Heart className="mr-2 h-4 w-4" />Lista desideri</DropdownMenuItem>
              {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link"><Shield className="mr-2 h-4 w-4" />Pannello admin</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout"><LogOut className="mr-2 h-4 w-4" />Esci</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link to={`/accedi?next=${encodeURIComponent(location.pathname + location.search)}`} data-testid="nav-login-button" className="flex items-center gap-1.5 rounded-full bg-black/5 px-4 py-1.5 text-sm font-semibold hover:bg-black/10">
            <User className="h-4 w-4" /><span className="hidden sm:inline">Accedi</span>
          </Link>
        )}
      </div>
      <div className="border-y-2 border-black bg-[#F6E7CB]">
      <nav className="flex w-full items-center gap-1 overflow-x-auto px-3 py-1 scrollbar-none sm:px-6 md:pl-36" data-testid="nav-menu" onClick={() => setShowSearch(false)}>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger className="mr-1 flex shrink-0 items-center gap-1.5 rounded-full border-2 border-black bg-amber-500 px-3 py-1.5 font-display text-sm text-black" data-testid="nav-menu-trigger">
            <Menu className="h-4 w-4" />Menu
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[calc(var(--radix-dropdown-menu-content-available-height)-4.5rem)] w-60 overflow-y-auto border-2 border-black bg-white sm:max-h-[var(--radix-dropdown-menu-content-available-height)]" data-testid="nav-menu-dropdown">
            <p className="px-2 pb-1 pt-2 text-[11px] uppercase tracking-[0.2em] text-stone-500">Giochi trattati</p>
            {GAME_PAGES.map((g) => (
              <DropdownMenuItem key={g.slug} onClick={() => navigate(`/gioco/${g.slug}`)} className="font-display" data-testid={`menu-game-${g.slug}`}>{g.name}</DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => navigate("/catalogo?category=accessori")} className="font-display" data-testid="menu-accessories">Accessori</DropdownMenuItem>
            {ACCESSORY_TYPES.map((t) => (
              <DropdownMenuItem key={t.value} onClick={() => navigate(`/catalogo?category=accessori&subcategory=${t.value}`)} className="pl-6 text-sm text-stone-600" data-testid={`menu-accessories-${t.value}`}>{t.label}</DropdownMenuItem>
            ))}
            <DropdownMenuItem onClick={() => navigate("/catalogo")} className="font-display" data-testid="nav-catalog">Tutto il catalogo</DropdownMenuItem>
            <DropdownMenuSeparator />
            <p className="px-2 pb-1 pt-1 text-[11px] uppercase tracking-[0.2em] text-stone-500">Servizi</p>
            {EXTRA_LINKS.map(([to, label, testid]) => (
              <DropdownMenuItem key={to} onClick={() => navigate(to)} className="font-display" data-testid={testid}>{label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {BAR_LINKS.map(([to, label, testid]) => (
          <Link key={to} to={to} data-testid={testid} className="shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 font-display text-sm text-stone-800 transition-colors hover:bg-black/10">{label}</Link>
        ))}
      </nav>
      </div>
    </header>
  );
};

const BAR_LINKS = [
  ["/deckbuilder", "Costruisci un mazzo", "navbar-deckbuilder"],
  ["/catalogo?sort=recenti", "Nuove uscite", "navbar-new"],
  ["/catalogo?category=accessori", "Accessori", "navbar-accessories"],
  ["/vendi", "Vendi le tue carte", "navbar-sell"],
  ["/eventi", "Eventi e tornei", "navbar-events"],
];

const EXTRA_LINKS = [
  ["/pezzi-da-collezione", "Pezzi da collezione", "nav-collection"],
  ["/eventi", "Eventi", "nav-events"],
  ["/news", "News", "nav-news"],
  ["/vendi", "Vendi le tue carte", "nav-sell"],
  ["/deckbuilder", "Deck builder", "nav-deckbuilder"],
  ["/b2b", "Area B2B", "nav-b2b"],
  ["/assistenza", "Assistenza e FAQ", "nav-support"],
];
